import { i18n } from '@/helpers/i18n';

/**
 * Extracts power from a solar panel item
 * @param {Object} item - The item object containing solar panel data
 * @returns {number|null} - Power in W or null if not available
 */
export const solarPanelPower = (item) => {
  return item?.solarPanel?.power || null;
};

/**
 * Calculates total power for a single solar panel (power * count)
 * @param {Object} panel - Panel object with item and count properties
 * @returns {string|null} - Formatted total power in kW or null if power not available
 */
export const solarPanelsTotalPower = (panel) => {
  const power = solarPanelPower(panel.item);
  if (!power) return 0;
  return (power / 1000) * parseInt(panel.count || 1, 10); // Convert W to kW
};

export const formatSolarPanelsTotalPower = (panel) => {
  return i18n.n(solarPanelsTotalPower(panel), 'decimal_triple');
};

/**
 * Calculates total power for all solar panels in a project's solar plant
 * @param {Array} solarPanels - Array of solar panel objects
 * @returns {string} - Formatted total power in kW
 */
export const solarPlantTotalPanelPower = (solarPanels) => {
  if (!solarPanels || !Array.isArray(solarPanels)) {
    return i18n.n(0, 'decimal_triple');
  }

  const total = solarPanels.reduce((sum, panel) => {
    const power = solarPanelPower(panel.item) || 0;
    const count = parseInt(panel.count || 0, 10);
    return sum + (power / 1000) * count; // Convert W to kW
  }, 0);

  return total;
};

export const formatSolarPlantTotalPanelPower = (solarPanels) => {
  return i18n.n(solarPlantTotalPanelPower(solarPanels), 'decimal_triple');
};

/**
 * Extracts capacity from a battery item
 * @param {Object} item - The item object containing battery data
 * @returns {number|null} - Capacity in kWh or null if not available
 */
export const batteryCapacity = (item) => {
  return item?.battery?.capacity || null;
};

/**
 * Calculates total capacity for a single battery entry (capacity * count)
 * @param {Object} battery - Battery object with item and count properties
 * @returns {number} - Total capacity in kWh
 */
export const batteriesTotalCapacity = (battery) => {
  const capacity = batteryCapacity(battery.item);
  if (!capacity) return 0;
  return capacity * parseInt(battery.count || 1, 10);
};

export const formatBatteriesTotalCapacity = (battery) => {
  return i18n.n(batteriesTotalCapacity(battery), 'decimal_single');
};

/**
 * Calculates total capacity for all batteries in a project's solar plant
 * @param {Array} batteries - Array of battery objects
 * @returns {number} - Total capacity in kWh
 */
export const solarPlantTotalBatteryCapacity = (batteries) => {
  if (!batteries || !Array.isArray(batteries)) {
    return 0;
  }

  return batteries.reduce((sum, battery) => {
    const capacity = batteryCapacity(battery.item) || 0;
    const count = parseInt(battery.count || 0, 10);
    return sum + capacity * count;
  }, 0);
};

export const formatSolarPlantTotalBatteryCapacity = (batteries) => {
  return i18n.n(solarPlantTotalBatteryCapacity(batteries), 'decimal_single');
};

/**
 * Extracts max net input power from a wallbox item
 * @param {Object} item - The item object containing wallbox data
 * @returns {number|null} - Power in kW or null if not available
 */
export const wallboxPower = (item) => {
  return item?.wallbox?.maxNetInputPower || null;
};

/**
 * Calculates total power for a single wallbox entry (power * count)
 * @param {Object} wallbox - Wallbox object with item and count properties
 * @returns {number} - Total power in kW
 */
export const wallboxesTotalPower = (wallbox) => {
  const power = wallboxPower(wallbox.item);
  if (!power) return 0;
  return power * parseInt(wallbox.count || 1, 10);
};

export const formatWallboxesTotalPower = (wallbox) => {
  return i18n.n(wallboxesTotalPower(wallbox), 'decimal_single');
};

/**
 * Calculates total power for all wallboxes in a project's solar plant
 * @param {Array} wallboxes - Array of wallbox objects
 * @returns {number} - Total power in kW
 */
export const solarPlantTotalWallboxPower = (wallboxes) => {
  if (!wallboxes || !Array.isArray(wallboxes)) {
    return 0;
  }

  return wallboxes.reduce((sum, wallbox) => {
    const power = wallboxPower(wallbox.item) || 0;
    const count = parseInt(wallbox.count || 0, 10);
    return sum + power * count;
  }, 0);
};

export const formatSolarPlantTotalWallboxPower = (wallboxes) => {
  return i18n.n(solarPlantTotalWallboxPower(wallboxes), 'decimal_single');
};

/**
 * Calculates total nominal power of all inverters in kW.
 * @param {Array} inverters - Array of inverter objects
 * @returns {number} - Total nominal power in kW
 */
export const inverterTotalNominalPower = (inverters) => {
  if (!inverters || !Array.isArray(inverters)) return 0;

  return inverters.reduce((total, inverter) => {
    const power = inverter.item?.inverter?.nominalPower || 0;
    const count = parseInt(inverter.count || 0, 10);
    return total + power * count;
  }, 0);
};

/**
 * Formats total nominal power of all inverters in kW.
 * @param {Array} inverters - Array of inverter objects
 * @returns {string} - Formatted nominal power in kW
 */
export const formatInverterTotalNominalPower = (inverters) => {
  return i18n.n(inverterTotalNominalPower(inverters), 'decimal_single');
};

/**
 * Calculates total apparent power of all inverters in kW.
 * @param {Array} inverters - Array of inverter objects
 * @returns {number} - Total apparent power in kW
 */
export const inverterTotalApparentPower = (inverters) => {
  if (!inverters || !Array.isArray(inverters)) return 0;

  return inverters.reduce((total, inverter) => {
    const power = inverter.item?.inverter?.apparentPower || 0;
    const count = parseInt(inverter.count || 0, 10);
    return total + power * count;
  }, 0);
};

/**
 * Formats total apparent power of all inverters in kW.
 * @param {Array} inverters - Array of inverter objects
 * @returns {string} - Formatted apparent power in kW
 */
export const formatInverterTotalApparentPower = (inverters) => {
  return i18n.n(inverterTotalApparentPower(inverters), 'decimal_single');
};

/**
 * Calculates total active power of all inverters in kW.
 * @param {Array} inverters - Array of inverter objects
 * @returns {number} - Total active power in kW
 */
export const inverterTotalActivePower = (inverters) => {
  if (!inverters || !Array.isArray(inverters)) return 0;

  return inverters.reduce((total, inverter) => {
    const power = inverter.item?.inverter?.activePower || 0;
    const count = parseInt(inverter.count || 0, 10);
    return total + power * count;
  }, 0);
};

/**
 * Formats total active power of all inverters in kW.
 * @param {Array} inverters - Array of inverter objects
 * @returns {string} - Formatted active power in kW
 */
export const formatInverterTotalActivePower = (inverters) => {
  return i18n.n(inverterTotalActivePower(inverters), 'decimal_single');
};

/**
 * Determines if a management system is required for the solar plant configuration
 * @param {Object} solarPlant - The solar plant configuration object
 * @returns {boolean} - True if management system is required, false otherwise
 */
export const management_system_required = (solarPlant) => {
  if (!solarPlant) return false;

  // Check inverter total power >= 7kW
  const inverterTotalPower = inverterTotalNominalPower(solarPlant.inverters);
  if (inverterTotalPower >= 7) return true;

  // Check solar panel total power >= 7kW
  const solarPanelTotalPower = solarPlantTotalPanelPower(solarPlant.solarPanels);
  if (solarPanelTotalPower >= 7) return true;

  // Check if battery exists
  if (solarPlant.battery) return true;
  if (Array.isArray(solarPlant.batteries) && solarPlant.batteries.length > 0) return true;

  // Check if wallbox exists
  if (solarPlant.wallbox) return true;
  if (Array.isArray(solarPlant.wallboxes) && solarPlant.wallboxes.length > 0) return true;

  return false;
};
